import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import { useAdminUi } from './AdminUiProvider'
import type { AdminTransactionEvent, AdminTransactionRow } from './adminTypes'
import { formatWhen } from './adminUtils'

type Props = {
  token: string
  transactionId: number
  onClose: () => void
  onUpdated?: () => void
}

export default function AdminTransactionDrawer({ token, transactionId, onClose, onUpdated }: Props) {
  const { t, i18n } = useTranslation()
  const { toast, confirm } = useAdminUi()
  const [detail, setDetail] = useState<AdminTransactionRow | null>(null)
  const [events, setEvents] = useState<AdminTransactionEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    Promise.all([
      adminAPI.getTransaction(token, transactionId),
      adminAPI.transactionHistory(token, transactionId),
    ])
      .then(([detailRes, historyRes]) => {
        setDetail(detailRes.data as AdminTransactionRow)
        setEvents((historyRes.data as AdminTransactionEvent[]) ?? [])
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false))
  }, [token, transactionId])

  useEffect(() => {
    load()
  }, [load])

  async function refund() {
    const ok = await confirm({
      title: t('admin.refund'),
      message: t('admin.refundConfirm'),
      danger: true,
      confirmLabel: t('admin.refund'),
    })
    if (!ok) return
    setBusy(true)
    adminAPI
      .refundTransaction(token, transactionId, { reason: 'admin_refund' })
      .then(() => {
        toast.success(t('admin.toast.refunded'))
        load()
        onUpdated?.()
      })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  function resolve(outcome: 'refund' | 'complete') {
    setBusy(true)
    adminAPI
      .resolveDispute(token, transactionId, { outcome, reason: 'admin_dispute_resolution' })
      .then(() => {
        toast.success(t('admin.toast.disputeResolved'))
        load()
        onUpdated?.()
      })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  return (
    <div className="vc-admin-drawer" role="dialog" aria-modal="true">
      <button type="button" className="vc-admin-drawer__backdrop" onClick={onClose} aria-label="Close" />
      <aside className="vc-admin-drawer__panel">
        <header className="vc-admin-drawer__head">
          <div>
            <h2>{t('admin.transaction')} #{transactionId}</h2>
            {detail ? (
              <p className="vc-admin-muted">
                {detail.canonical_state} · €{detail.amount.toFixed(2)}
              </p>
            ) : null}
          </div>
          <button type="button" className="vc-admin-drawer__close" onClick={onClose}>
            ×
          </button>
        </header>

        {error ? <p className="vc-admin-error">{error}</p> : null}
        {loading ? <p className="meta-text">{t('common.loading')}</p> : null}

        {detail ? (
          <>
            <section className="vc-admin-drawer__section">
              <dl className="vc-admin-dl">
                <div>
                  <dt>{t('admin.buyer')}</dt>
                  <dd>{detail.buyer_email}</dd>
                </div>
                <div>
                  <dt>{t('admin.seller')}</dt>
                  <dd>{detail.seller_email}</dd>
                </div>
                {detail.listing_title ? (
                  <div>
                    <dt>{t('admin.listing')}</dt>
                    <dd>{detail.listing_title}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>{t('admin.created')}</dt>
                  <dd>{formatWhen(detail.created_at, i18n.language)}</dd>
                </div>
              </dl>
            </section>

            <section className="vc-admin-drawer__section">
              <h3>{t('admin.timeline')}</h3>
              {events.length === 0 ? (
                <p className="vc-admin-muted">{t('admin.noTimeline')}</p>
              ) : (
                <ol className="vc-admin-timeline">
                  {events.map((event) => (
                    <li key={event.id}>
                      <span className="vc-admin-timeline__time">
                        {formatWhen(event.created_at, i18n.language)}
                      </span>
                      <strong>
                        {event.from_state} → {event.to_state}
                      </strong>
                      <span className="vc-admin-muted">
                        {event.actor_type}
                        {event.reason ? ` · ${event.reason}` : ''}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <div className="vc-admin-drawer__actions">
              <button
                type="button"
                className="vc-admin-btn vc-admin-btn--danger"
                disabled={busy}
                onClick={refund}
              >
                {t('admin.refund')}
              </button>
              <button
                type="button"
                className="vc-admin-btn vc-admin-btn--ghost"
                disabled={busy}
                onClick={() => resolve('refund')}
              >
                {t('admin.resolveRefund')}
              </button>
              <button
                type="button"
                className="vc-admin-btn vc-admin-btn--primary"
                disabled={busy}
                onClick={() => resolve('complete')}
              >
                {t('admin.resolveComplete')}
              </button>
            </div>
          </>
        ) : null}
      </aside>
    </div>
  )
}
